import { useCallback, useEffect, useState } from "react";
import { partnerApi, centsToUsd, bpsToPct } from "@/lib/partner";
import { PartnerShell, PartnerLoading, PartnerEmpty, PartnerError } from "@/components/partner/PartnerShell";

const STATUSES = ["all", "pending", "approved", "payable", "paid", "reversed"];
const STATUS_COLOR = {
  pending: "border-white/15 text-white/70",
  approved: "border-sky-500/40 text-sky-300",
  payable: "border-[#D6A653]/50 text-[#D6A653]",
  paid: "border-emerald-500/40 text-emerald-300",
  reversed: "border-red-500/40 text-red-300",
};

export default function PartnerCommissions() {
  const [status, setStatus] = useState("all");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setErr("");
    const params = { limit: 100, offset: 0 };
    if (status !== "all") params.status = status;
    partnerApi
      .commissions(params)
      .then((d) => {
        setItems(Array.isArray(d?.items) ? d.items : []);
        setTotal(d?.total || 0);
      })
      .catch((e) => setErr(e?.response?.data?.detail || "load_failed"))
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  return (
    <PartnerShell testId="partner-commissions-page">
      <h1 className="text-3xl font-light tracking-tight">Commission history</h1>
      <p className="mt-2 text-sm text-white/50">
        Every commissioned payment. Reversals appear as separate rows or as negative clawbacks —
        original records are never mutated.
      </p>

      <div className="mt-6 flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`filter-${s}`}
            className={`rounded-full border px-3 py-1.5 text-xs transition-colors capitalize ${
              status === s
                ? "border-[#D6A653] bg-[#D6A653]/12 text-[#D6A653]"
                : "border-white/12 text-white/55 hover:text-white"
            }`}
          >
            {s}
          </button>
        ))}
        <span className="ml-auto self-center text-[11px] text-white/40">{total} total</span>
      </div>

      {loading ? (
        <PartnerLoading testId="partner-commissions-loading" />
      ) : err ? (
        <PartnerError
          testId="partner-commissions-error"
          message={err === "not_a_partner" ? "You’re not a partner yet." : "We couldn’t load your commissions."}
          onRetry={err === "not_a_partner" ? undefined : load}
        />
      ) : items.length === 0 ? (
        <PartnerEmpty
          testId="partner-commissions-empty"
          title="No commissions yet"
          sub="When your referred customers pay, commissions will appear here."
        />
      ) : (
        <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10 bg-[#0B0D12]">
          <table className="min-w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-white/40">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Invoice</th>
                <th className="px-4 py-3 text-right">Amount paid</th>
                <th className="px-4 py-3 text-right">Rate</th>
                <th className="px-4 py-3 text-right">Commission</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Source</th>
              </tr>
            </thead>
            <tbody className="text-white/80">
              {items.map((c) => (
                <tr key={c?.id} className="border-t border-white/5" data-testid={`comm-${c?.id}`}>
                  <td className="px-4 py-3 text-xs text-white/50">{c?.created_at ? new Date(c.created_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-white/60">{c?.stripe_invoice_id || "—"}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{centsToUsd(c?.amount_paid_cents ?? 0, c?.currency)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{bpsToPct(c?.commission_rate_bps ?? 0)}</td>
                  <td className={`px-4 py-3 text-right tabular-nums ${(c?.commission_cents ?? 0) < 0 ? "text-red-300" : ""}`}>
                    {centsToUsd(c?.commission_cents ?? 0, c?.currency)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-2 py-0.5 text-[11px] capitalize ${STATUS_COLOR[c?.status] || "border-white/15 text-white/60"}`}>
                      {c?.status || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-white/50 capitalize">{(c?.attribution_source || "").replace("_", " ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PartnerShell>
  );
}
